// PARTNER-PORTÁL · WYSIWYG-KÉP: feltöltés és megjelenítés-feloldás.
//
// A folyamat alakja a belső oldallal AZONOS (upload-url → közvetlen PUT a tárba → commit), és a validálás is
// ugyanaz — de MÁS VÉGPONTOKRA megy, ahol a cél-fiók KÖTELEZŐ és a tenant a hívó portál-hozzáféréséből
// oldódik fel. A belső út „fiók nélkül a MORGENS ügyfél a gazda" fallbackja a partnernél elvi hiba lenne:
// a partner írása nem kerülhet a mi tenantunkba.
//
// ══ A MEGJELENÍTÉS KÉT ESETE — ÉS MIÉRT KELL A SEED ══════════════════════════════════════════════════════
// A szerkesztőbe beszúrt kép a STABIL utat viseli (`/editor-images/{fileId}/content`), amit a böngésző nem
// tud betölteni (a BFF Bearer-tokent kér, egy `<img>` nem küld fejlécet). A feloldáshoz kell egy végpont —
// csak épp:
//   • egy MENTETT jegyen a jegyhez KÖTÖTT feloldó a helyes (`GET /portal/tickets/:id/images/:fileId`), ami
//     csak akkor ad URL-t, ha a fájl-id tényleg a látható jegy szövegében szerepel;
//   • egy MÉG NEM LÉTEZŐ jegy szerkesztőjében nincs mihez kötni. Ezért a `commit` válasza egy azonnali
//     `displayUrl`-t is ad, és azt ITT, a modul-szintű seed-térképben tartjuk — a szerkesztő feloldója
//     ebből szolgál ki, hálózat nélkül.
//
// A seed SOHA nem kerül a mentett HTML-be: a szerkesztő a stabil utat menti, a `displayUrl` csak a lap
// életében élő előnézet.

import { api } from '@/lib/api';
import type { EditorImageResolverConfig } from '@/lib/editor-image-src';
import type { EditorImageUploadResult } from '@/lib/editor-image-upload';
import { prepareEditorMedia } from '@/lib/editor-media-prepare';

// A TÍPUS-WHITELIST és a (TÍPUS-FÜGGŐ) MÉRET-PLAFON a SZERZŐDÉSBŐL jön — ugyanaz, amit a szerver validál.

/**
 * fileId → AZONNALI megjelenítési URL, a `commit` válaszából.
 *
 * MODUL-SZINTŰ, mert a szerkesztő node-view-ja React-propokat nem lát, és a lap-újrarenderelést is túl kell
 * élnie. A tartalma rövid életű presigned URL — a lap elhagyásával elévül, és ez rendben van: a mentés után
 * a jegyhez kötött feloldó szolgál ki.
 */
const seeded = new Map<string, string>();

/**
 * Kép feltöltése a partner-portálról. SOHA nem dob: minden ág `EditorImageUploadResult`-ra képződik.
 *
 * Az `accountId` KÖTELEZŐ (a jegy-nyitó 1. lépése épp ezt választja ki) — üresen a hívás el sem indul, mert
 * a szerver 422-t adna, és a felhasználó egy értelmezhetetlen hibát látna a kép-gomb után.
 */
export async function uploadPortalEditorImage(
  original: File,
  accountId: string,
): Promise<EditorImageUploadResult> {
  // típus → (nagy fotónál kicsinyítés) → méret — egy közös szabály a két feltöltőnek
  const prepared = await prepareEditorMedia(original);
  if (!prepared.ok) return { ok: false, error: prepared.error };
  const { file, contentType } = prepared;
  if (accountId === '') return { ok: false, error: 'failed' };

  try {
    const signed = await api.portal.editorImageUploadUrl.mutation({
      body: { accountId, filename: file.name, contentType, sizeBytes: file.size },
    });
    if (signed.status !== 200) return { ok: false, error: 'failed' };

    // A presigned PUT a TÁRBA megy — a `Content-Type` egyeznie kell az aláírásban szereplővel.
    const put = await fetch(signed.body.uploadUrl, {
      method: 'PUT',
      headers: { 'content-type': contentType },
      body: file,
    });
    if (!put.ok) return { ok: false, error: 'failed' };

    const committed = await api.portal.editorImageCommit.mutation({
      params: { fileId: signed.body.fileId },
      body: { accountId },
    });
    if (committed.status !== 200) return { ok: false, error: 'failed' };
    // Az AZONNALI előnézet URL-je a seed-térképbe (a szerkesztő feloldója ebből szolgál ki). Üres
    // `displayUrl` (a presign nem oldható fel) esetén nem seedelünk: a kép feltöltve van, csak az előnézet
    // marad üres — a mentés után a jegyhez kötött feloldó megjeleníti.
    if (committed.body.displayUrl !== '') {
      seeded.set(signed.body.fileId, committed.body.displayUrl);
    }
    return { ok: true, url: committed.body.url };
  } catch {
    // Hálózati/parse-hiba → generikus jelzés (nyers hiba SOHA nem megy a felhasználóhoz).
    return { ok: false, error: 'failed' };
  }
}

/**
 * A JEGY-NYITÓ szerkesztőjének feloldója: KIZÁRÓLAG a most feltöltött (seedelt) képeket ismeri.
 *
 * MIÉRT NINCS hálózati fallback: a még nem létező jegy képéhez nincs mit kötni, egy „bármit feloldó" végpont
 * pedig épp az a lyuk, amit a szűk feloldókkal zárunk. Nem seedelt id → `null`, azaz a kép `src` nélkül
 * marad (nem villog törött kép).
 */
export const PORTAL_NEW_TICKET_IMAGE_RESOLVER: EditorImageResolverConfig = {
  scope: 'portal-new-ticket',
  resolve: (fileId) => Promise.resolve(seeded.get(fileId) ?? null),
};

/**
 * Egy MENTETT jegy tartalmának kép-feloldója. A `scope` a jegyet is tartalmazza: ugyanaz a fájl-id más
 * jegy alatt más (vagy semmilyen) URL-t kap, tehát nem oszthatják a cache-t.
 */
export function portalTicketImageResolver(ticketId: string): EditorImageResolverConfig {
  return {
    scope: `portal-ticket:${ticketId}`,
    // A SEEDET ITT SZÁNDÉKOSAN NEM NÉZZÜK, pedig a frissen feltöltött kép benne van, és egy hálózati kört
    // megspórolna. MIÉRT NEM: a seed egy RÖVID ÉLETŰ presigned URL, ami a lap életében nem újul meg — egy
    // nyitva hagyott jegy-lapon órák múlva 403-cal törne el a kép, és nem lenne mi újrapróbálja (a
    // `resolveEditorImageSrc` cache-e is csak ugyanazt az elévült seedet olvasná vissza). A jegyhez kötött
    // végpont ehelyett MINDIG friss URL-t ad, és a feloldó 5 perces cache-e amúgy is elnyeli a forgalmat.
    // (Mérve teszttel: a seed-elsőbbség miatt a mentett jegyen a szerkesztő-előnézet URL-je
    // jelent meg a friss helyett.)
    resolve: async (fileId) => {
      const res = await api.portal.ticketImage.query({ params: { id: ticketId, fileId } });
      return res.status === 200 ? res.body.url : null;
    },
  };
}
